import { get } from 'lodash-es';
import { emptyArray } from 'ui-components/default-props/empty';
import { ProviderHelperService } from '../../services/provider-helper-service';
import { ShippingServiceService } from '../../services/shipping-service-service';
import Global from '../global-selectors';
import { getCarrierDisplayName } from '../../components/data-driven-logo/get-carrier-display-name';
import { getCachedNormalizedBYOLProviders } from '../../hooks/use-normalized-BYOL-providers';

const getFilteredServices = (state, props) => {
  const filters = get(props, 'filters', emptyArray);
  return Global.selectSellerServices(state)
    .filter((service) => !service.isHidden)
    .filter((service) => filters.every((filter) => filter(service)));
};

const findPrimaryAccount = (sellerLabelProviders, selectedGroupId) => {
  return (
    sellerLabelProviders.find((slp) => slp.isPrimaryAccount) ||
    sellerLabelProviders.find((slp) => slp.sellerLabelProviderId === selectedGroupId) ||
    sellerLabelProviders[0]
  );
};

const buildCarrierDataNoSelectedProvider = (carrierId, providers, sellerLabelProviders, fromCSW) => {
  const matchingProviders = providers.filter((provider) => provider.carrierId === carrierId);
  const uniqueLabelProviderIds = [...new Set(matchingProviders.map((p) => p.labelProviderId))];
  const uniqueAndDefault = ShippingServiceService.getUniqueSellerLabelProviders(uniqueLabelProviderIds, sellerLabelProviders);
  const soloOrDefault =
    uniqueAndDefault.find((p) => p.isPrimaryAccount && p.walletId) ||
    uniqueAndDefault.find((p) => p.isPrimaryAccount) ||
    uniqueAndDefault[0];
  const labelProviderId = get(soloOrDefault, 'labelProviderId', 0);
  const firstSellerLabelProviderId = get(soloOrDefault, 'sellerLabelProviderId');
  const selectedComparer = fromCSW ? firstSellerLabelProviderId : labelProviderId;
  return {
    labelProviderId,
    firstSellerLabelProviderId,
    selectedComparer,
  };
};

const buildRankedCarrierData = (filteredSellerProviders, baseCarrierObj, serviceMap, selectedGroupId, fromCSW) => {
  const soloOrDefault = findPrimaryAccount(filteredSellerProviders, selectedGroupId);
  const selectedComparer = fromCSW ? soloOrDefault.sellerLabelProviderId : soloOrDefault.labelProviderId;
  return {
    ...baseCarrierObj,
    serviceGroupId: soloOrDefault.sellerLabelProviderId,
    services: serviceMap[soloOrDefault.labelProviderId] || emptyArray,
    selected: selectedComparer === selectedGroupId,
  };
};

const buildCarrierDataWithSelectedProvider = (selectedProvider, selectedGroupId, sellerLabelProviders, fromCSW) => {
  const { labelProviderId } = selectedProvider;
  const { sellerLabelProviderId } = sellerLabelProviders.find((slp) => slp.sellerLabelProviderId === selectedGroupId) || {};
  const selectedComparer = fromCSW ? sellerLabelProviderId : labelProviderId;
  return {
    labelProviderId,
    sellerLabelProviderId,
    selectedComparer,
  };
};

const groupByToServiceGroupMap = {
  Carrier: (state, props) => {
    const { selectedServiceId, fromCSW } = props;
    let { selectedGroupId } = props;
    const carriers = Global.selectReferenceDatagetSellerCarriers(state);
    const sellerLabelProviders = Global.selectSellerLabelProviders(state);
    const providers = Global.selectReferenceDatagetProviders(state);
    const services = getFilteredServices(state, props);
    const selectedServiceProviderId =
      selectedServiceId && services.find((service) => service.serviceId === selectedServiceId)
        ? services.find((service) => service.serviceId === selectedServiceId).labelProviderId
        : null;
    const selectedProvider = providers.find((p) => p.labelProviderId === selectedServiceProviderId);
    const serviceMap = ShippingServiceService.buildServiceMap(services, sellerLabelProviders);
    if (!selectedGroupId && selectedServiceId) {
      const matchingServices = services.filter((service) => service.serviceId === selectedServiceId);
      if (matchingServices.length > 0) {
        const matchingSellerProvider = findPrimaryAccount(
          sellerLabelProviders.filter((slp) => slp.labelProviderId === matchingServices[0].labelProviderId),
        );
        selectedGroupId = fromCSW ? get(matchingSellerProvider, 'sellerLabelProviderId') : get(matchingSellerProvider, 'labelProviderId');
      }
    }
    return carriers.map((carrier) => {
      const { carrierId } = carrier;
      const baseCarrierObj = {
        serviceGroupType: 'carrier',
        serviceGroupTypeObject: carrier,
        name: getCarrierDisplayName(carrierId),
      };

      if (selectedProvider && selectedProvider.carrierId === carrierId) {
        const { labelProviderId, sellerLabelProviderId, selectedComparer } = buildCarrierDataWithSelectedProvider(
          selectedProvider,
          selectedGroupId,
          sellerLabelProviders,
          fromCSW,
        );
        return {
          ...baseCarrierObj,
          serviceGroupId: sellerLabelProviderId,
          services: serviceMap[labelProviderId] || emptyArray,
          selected: selectedComparer === selectedGroupId,
        };
      }

      if (!selectedProvider && carrierId === '1') {
        const expressOne = sellerLabelProviders.filter((p) => p.labelProviderId === '1');
        if (expressOne.length > 0) {
          return buildRankedCarrierData(expressOne, baseCarrierObj, serviceMap, selectedGroupId, fromCSW);
        }

        const stamps = sellerLabelProviders.filter((p) => p.labelProviderId === '2');
        if (stamps.length > 0) {
          return buildRankedCarrierData(stamps, baseCarrierObj, serviceMap, selectedGroupId, fromCSW);
        }

        const endicia = sellerLabelProviders.filter((p) => p.labelProviderId === '5');
        if (endicia.length > 0) {
          return buildRankedCarrierData(endicia, baseCarrierObj, serviceMap, selectedGroupId, fromCSW);
        }
      }

      const { labelProviderId, firstSellerLabelProviderId, selectedComparer } = buildCarrierDataNoSelectedProvider(
        carrierId,
        providers,
        sellerLabelProviders,
        fromCSW,
      );
      return {
        ...baseCarrierObj,
        serviceGroupId: firstSellerLabelProviderId,
        services: serviceMap[labelProviderId] || emptyArray,
        selected: selectedComparer === selectedGroupId,
      };
    });
  },
  Provider: (state, props) => {
    const { selectedGroupId, shouldShowPrimaryOnly } = props;
    const BYOLreferenceDataProviders = getCachedNormalizedBYOLProviders();
    const sellerLabelProviders = Global.selectSellerLabelProviders(state);
    const providers = Global.selectReferenceDatagetProviders(state);
    const services = getFilteredServices(state, props);
    return sellerLabelProviders.reduce((filtered, provider) => {
      if (BYOLreferenceDataProviders[provider.labelProviderId] || (shouldShowPrimaryOnly && !provider.isPrimaryAccount)) {
        return filtered;
      }
      const { sellerLabelProviderId, labelProviderId } = provider;
      return [
        ...filtered,
        {
          serviceGroupType: 'seller-label-provider',
          serviceGroupId: sellerLabelProviderId,
          serviceGroupTypeObject: provider,
          name: ProviderHelperService.getSellerLabelProviderName(provider, providers, sellerLabelProviders),
          services: services.filter((service) => service.sellerLabelProviderId === sellerLabelProviderId),
          selected: sellerLabelProviderId === selectedGroupId,
          isPrimaryAccount: provider.isPrimaryAccount,
          groupId: labelProviderId,
        },
      ];
    }, []);
  },
};

export const getGroupedShippingServicesForType = (shippingDropdownType, state, props) => {
  const groupFunction = groupByToServiceGroupMap[shippingDropdownType] || groupByToServiceGroupMap.Provider;
  return groupFunction(state, props);
};
